import { ChatMessage } from "../api";
import { PromptTemplate } from "../models/PromptTemplate";

/**
 * Strips embedded prompt-template content from user messages loaded out of
 * conversation history, replacing it with the original slash-command form
 * (e.g. "/prd some user input").
 *
 * ## Background — why this exists
 *
 * Before the server-side template resolution fix, the UI expanded slash commands
 * into "[full template body]\n\n[user input]" before calling the backend.  The
 * backend persisted that expanded form.  On reload we must reverse the substitution
 * so the raw template text is never visible to the user.
 *
 * ## Version-safety
 *
 * After the fix, new messages no longer embed template content — the backend handles
 * injection server-side and the history endpoint strips legacy content before
 * returning it.  This function is kept as a defence-in-depth measure for any
 * messages that may have been persisted with old content and returned by the API
 * before the server-side fix is deployed.
 *
 * To handle messages that were created under a different (now superseded) template
 * version, callers may pass `allVersionContents` — a map from promptKey to every
 * known version content string.  When provided, all version contents are checked
 * rather than only the current active content.
 *
 * ## Idempotency
 *
 * The function is idempotent: messages that have already been stripped (or that
 * never contained template content) are returned unchanged.
 *
 * ## Performance
 *
 * If no message was actually modified the *original* array reference is returned
 * so callers can use reference-equality to skip unnecessary state updates.
 */
export const stripTemplateContent = (
  messages: ChatMessage[],
  knownTemplates: PromptTemplate[],
  /** Optional map of promptKey → all historical version content strings for backward compat */
  allVersionContents?: Map<string, string[]>
): ChatMessage[] => {
  if (!knownTemplates || knownTemplates.length === 0) return messages;

  let anyChanged = false;

  const result = messages.map((msg) => {
    if (msg.role !== "user") return msg;

    for (const tpl of knownTemplates) {
      // Build the list of content strings to check for this template.
      // Always include the current active content; append historical versions if available.
      const contentsToCheck: string[] = [];
      if (tpl.content) contentsToCheck.push(tpl.content);
      const historicalContents = allVersionContents?.get(tpl.promptKey) ?? [];
      for (const hc of historicalContents) {
        if (hc && !contentsToCheck.includes(hc)) contentsToCheck.push(hc);
      }

      for (const content of contentsToCheck) {
        const prefix = content + "\n\n";
        if (msg.content.startsWith(prefix)) {
          anyChanged = true;
          const userInput = msg.content.slice(prefix.length);
          return {
            ...msg,
            content: userInput ? `/${tpl.promptKey} ${userInput}` : `/${tpl.promptKey}`,
          };
        }
      }
    }

    return msg;
  });

  // Return the original reference when nothing changed so callers can do a
  // cheap reference-equality check to skip redundant state updates.
  return anyChanged ? result : messages;
};
